import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import SupportSidebar from "../../components/SignUp/SupportSidebar";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../components/shared/ToastProvider";
import { useTranslation } from "react-i18next";
import { useContext } from "react";
import Step2 from "../../components/SingnUpContractor/Step2";
import Step3 from "../../components/SingnUpContractor/Step3";
import { BsArrowLeft } from "react-icons/bs";
import Step4 from "../../components/SingnUpContractor/Step4";
import latestLogo from "../../assets/latestLogo.svg";
import { clearAuthSnapshot } from "../../utils/authutils";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigationType } from "react-router-dom";
import { useContractorUpdateMutation } from "./contractorQuery";
const steps = [Step2, Step3, Step4];

const initialValues = {
  sustainability_esg: null,
  insurance_liability: null,
  trade_specialization: "",
  other_trade_specialization: false,
  gdpr_data_processing: null,
  availability: "",
  response_time: "",
  project_capacity: "",
  about_company: "",
  previous_projects: null,
  client_testimonials: null,
  invoicing_schedule: "",
  payment_terms: "",
};

const validationSchemas = [
  // Yup.object({
  //   email: Yup.string().email("Invalid email").required("Required"),
  //   password: Yup.string().min(8, "Min 8 chars").required("Required"),
  //   confirmPassword: Yup.string()
  //     .oneOf([Yup.ref("password"), null], "Passwords must match")
  //     .required("Required"),
  //   first_name: Yup.string().required("Required"),
  //   last_name: Yup.string().required("Required"),
  //   phone_no: Yup.string().required("Required"),
  // }),
  Yup.object({
    sustainability_esg: Yup.mixed().nullable(),
    insurance_liability: Yup.mixed().required("Required"),
    trade_specialization: Yup.string().required("Required"),
    // other_trade_specialization is a boolean, no string validation needed
    gdpr_data_processing: Yup.mixed().nullable(),
  }),
  Yup.object({
    availability: Yup.string().required("Required"),
    response_time: Yup.string().required("Required"),
    project_capacity: Yup.number().required("Required"),
    about_company: Yup.string(),
  }),
  Yup.object({
    previous_projects: Yup.mixed().required("Required"),
    client_testimonials: Yup.mixed().required("Required"),
    invoicing_schedule: Yup.string().required("Required"),
    payment_terms: Yup.string().required("Required"),
  }),
];

const buildSignupPayload = (values) => {
  const formData = new FormData();
  // formData.append("email", values.email.trim());
  // formData.append("password", values.password);
  // formData.append("first_name", values.first_name.trim());
  // formData.append("last_name", values.last_name.trim());
  // formData.append("phone_no", values.phone_no.trim());
  if (values.sustainability_esg)
    formData.append("sustainability_esg", values.sustainability_esg);
  if (values.insurance_liability)
    formData.append("insurance_liability", values.insurance_liability);
  formData.append("trade_specialization", values.trade_specialization);
  formData.append(
    "other_trade_specialization",
    !!values.other_trade_specialization,
  );
  if (values.gdpr_data_processing)
    formData.append("gdpr_data_processing", values.gdpr_data_processing);
  formData.append("availability", values.availability);
  formData.append("response_time", values.response_time);
  formData.append("project_capacity", values.project_capacity);
  formData.append("about_company", values.about_company);
  if (values.previous_projects)
    formData.append("previous_projects", values.previous_projects);
  if (values.client_testimonials)
    formData.append("client_testimonials", values.client_testimonials);
  formData.append("invoicing_schedule", values.invoicing_schedule);
  formData.append("payment_terms", values.payment_terms);
  return formData;
};

const MultiStepSignUpContractor = () => {
  const [step, setStep] = useState(0);
  const StepComponent = steps[step];
  const isLast = step === steps.length - 1;
  const isFirst = step === 0;
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const { updateProfile } = useContext(AuthContext);

  const { mutate, isPending, isError, error, isSuccess, data } =
    useContractorUpdateMutation();

  const handleNext = async (values, actions) => {
    try {
      await validationSchemas[step].validate(values, { abortEarly: false });
      setStep((s) => s + 1);
    } catch (err) {
      if (err.inner) {
        const errors = {};
        const touched = {};
        err.inner.forEach((e) => {
          if (e.path && !errors[e.path]) {
            errors[e.path] = e.message;
            touched[e.path] = true;
          }
        });
        actions.setErrors(errors);
        actions.setTouched(touched, false);
      }
    }
  };

  const handleBack = () => setStep((s) => s - 1);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchemas[step]}
      validateOnBlur={true}
      validateOnChange={true}
      onSubmit={async (values, actions) => {
        if (!isLast) {
          await handleNext(values, actions);
          return;
        }

        try {
          actions.setStatus(undefined);
          actions.setSubmitting(true);

          const payload = buildSignupPayload(values);

          mutate(payload, {
            onSuccess: (response) => {
              const updatedProfile = response?.data || {};
              updateProfile({ ...updatedProfile, is_profile_completed: true });
              actions.setSubmitting(false);
              addToast("Profile Completed", "success");
              navigate("/dashboard");
            },
            onError: (err) => {
              actions.setSubmitting(false);
              actions.setStatus({
                apiError: err.message || "onboarding failed",
              });
            },
          });
        } catch (e) {
          actions.setSubmitting(false);
          actions.setStatus({ apiError: "Something went wrong" });
        }
      }}
    >
      {({ handleSubmit, isSubmitting, status }) => (
        <Form
          className="max-w-lg w-full p-0 rounded-xl flex flex-col max-h-400"
          style={{ maxHeight: "calc(100vh - 200px)" }}
          onSubmit={handleSubmit}
        >
          <div className="z-20 rounded-t-xl px-4 pt-4 pb-2">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                {steps.map((_, i) => (
                  <div
                    key={i}
                    className={`w-7 h-1.5 rounded transition-all duration-200 ${
                      i === step ? "bg-green-600" : "bg-gray-200"
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs text-gray-500">
                {t("contractorSignup.stepLabel", {
                  current: step + 1,
                  total: steps.length,
                  defaultValue: `Step ${step + 1} of ${steps.length}`,
                })}
              </span>
            </div>

            {(status?.apiError || (isError && error?.message)) && (
              <div className="mt-3 text-sm text-red-600">
                {status?.apiError || error?.message}
              </div>
            )}
            {isSuccess && data && (
              <div className="mt-3 text-sm text-green-600">
                {t(
                  "contractorSignup.accountCreatedSuccess",
                  "Account created successfully.",
                )}
              </div>
            )}
          </div>

          <div className="flex-1 flex flex-col min-h-0 overflow-hidden px-6 py-5 gap-4">
            <div className="rounded-lg bg-[#F6FBFF] p-0">
              <StepComponent />
            </div>
          </div>

          <div className="flex justify-between gap-3 mt-0 pt-2 z-20 px-4 pb-3">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-50 text-gray-700 font-medium border border-gray-200 hover:bg-gray-100 transition disabled:opacity-50"
              onClick={handleBack}
              disabled={isFirst || isPending || isSubmitting}
            >
              <div className="flex items-center gap-5 justify-center">
                <BsArrowLeft />
                <span className="text-sm">
                  {t("contractorSignup.goBack", "Go Back")}
                </span>
              </div>
            </button>
            <button
              type="submit"
              className="px-7 py-1 rounded bg-secondaryColor text-white text-sm border border-green-600 hover:bg-secondaryColor/90 transition disabled:opacity-50"
              disabled={isPending || isSubmitting}
            >
              {isLast ? (
                isPending || isSubmitting ? (
                  t("contractorSignup.submitting", "Submitting...")
                ) : (
                  t("contractorSignup.submit", "Submit")
                )
              ) : (
                <span className="inline-flex items-center gap-5">
                  <span>{t("contractorSignup.next", "Next")}</span>
                  <span>›</span>
                </span>
              )}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const ContractorSignUp = () => {
  const navigationType = useNavigationType();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useContext(AuthContext);

  useEffect(() => {
    if (navigationType === "POP" && !isAuthenticated) {
      // POP = browser back or forward
      clearAuthSnapshot();
    }
  }, [navigationType, isAuthenticated]);
  return (
    <div className="h-screen flex bg-[#F6FBFF] ">
      <div className="hidden md:flex flex-col pt-[20px]">
        <SupportSidebar />
      </div>
      <div className="flex-1 flex flex-col">
        <div className="w-full px-4 flex items-center justify-end">
          <div className="flex items-center m-4">
            <select
              className="h-9 text-base bg-[#F6FBFF] rounded px-3 py-1 ring-1 ring-green-500 focus:outline-none"
              value={i18n?.language}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
            >
              <option value="en" className="text-base">
                {t("contractorSignup.langEnglish", "English")}
              </option>
              <option value="da" className="text-base">
                {t("contractorSignup.langDanish", "Dansk")}
              </option>
            </select>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-4 overflow-hidden">
          <div className="w-full max-w-lg flex flex-col items-start justify-center gap-2">
            <>
              <div className="font-sans p-1 rounded-sm">
                <img
                  src={latestLogo}
                  alt="AssetEye Logo"
                  className="w-12 h-12"
                />
              </div>

              <h1 className="text-xl font-bold text-black mb-1 tracking-tight">
                {t("contractorSignup.titlePrefix", "Complete your details to")}
                <span className="text-[#3D6C50] ms-2">
                  {t("contractorSignup.titleSuffix", "Login.")}
                </span>
              </h1>
              <p className="text-gray-600 mb-2 text-xs">
                {t(
                  "contractorSignup.description",
                  "Complete your details to connect with AssetEye, Powered by Dronetjek.",
                )}
              </p>
            </>
            <MultiStepSignUpContractor />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorSignUp;
